// Per-tab keep-alive (docs/specs/2026-10-06-bo-taskbar-fill-keepalive-design.md
// §3). Every decision is here, vue-free, so jest can test it without mounting
// a .vue (reference-bo-fn-jest-infra); DashboardLayout.vue and router.js only
// wire it up.
//
// Same house rules as src/utils/taskbar.js: nothing here throws. These run on
// every navigation (beforeResolve) and every tab change; a bad route record or
// a keep-alive whose internals moved must cost a page that is not cached, never
// a navigation that does not happen.
import { taskbarIsPage } from "./taskbar";

// The route record the layout's <router-view> renders. Every backoffice route
// is two levels deep (layout -> page), so that is the last matched record.
export function leafRecord(route) {
	const matched = route && typeof route === "object" ? route.matched : null;
	if (!Array.isArray(matched) || !matched.length) return null;
	return matched[matched.length - 1] || null;
}

// The name keep-alive's `include` will see for this route component.
//
// keep-alive matches on Ctor.options.name, and most pages in src/views have no
// `name`. An anonymous OPTIONS OBJECT is given `fallback` as its name. A
// function is never written to: before vue-router resolves it it is a lazy
// loader, and a constructor's own `.name` is "VueComponent".
//
// The constructor(s) Vue has ALREADY built from that options object are named
// too. This is not optional: vue-router 3.1.6 calls Vue.extend on every
// activated route component before beforeResolve runs (extractGuard, to read
// beforeRouteEnter; resolveAsyncComponents for lazy ones), and Vue.extend
// caches the result on def._Ctor and copies `name` at that moment. Naming only
// the options object would leave the cached constructor anonymous, and the page
// would never be cached (caught by tests/unit/keepAliveIntegration.spec.js on
// the real router). Only a constructor that has NO name is touched.
function nameCachedCtors(def, name) {
	const ctors = def && def._Ctor;
	if (!ctors || typeof ctors !== "object") return;
	for (const id of Object.keys(ctors)) {
		const ctor = ctors[id];
		const opts = ctor && ctor.options;
		if (opts && typeof opts === "object" && !opts.name) opts.name = name;
	}
}

export function componentNameOf(def, fallback) {
	try {
		if (typeof def === "function") {
			// A bare function (an unresolved lazy loader) is never written to.
			// A Vue-built constructor (it carries `options`) with no name is
			// named in its options, as nameCachedCtors does — never Function
			// .name, which is "VueComponent". An existing name is never replaced.
			const opts = def.options;
			if (!opts || typeof opts !== "object") return "";
			if (typeof opts.name === "string" && opts.name !== "") return opts.name;
			if (typeof fallback === "string" && fallback !== "") {
				opts.name = fallback;
				return opts.name === fallback ? fallback : "";
			}
			return "";
		}
		if (!def || typeof def !== "object") return "";
		if (typeof def.name === "string" && def.name !== "") {
			nameCachedCtors(def, def.name);
			return def.name;
		}
		if (def.options && typeof def.options.name === "string" && def.options.name !== "") return def.options.name;
		if (typeof fallback === "string" && fallback !== "") {
			def.name = fallback;
			if (def.name !== fallback) return "";
			nameCachedCtors(def, fallback);
			return fallback;
		}
	} catch (e) {
		/* frozen / exotic object — not cacheable, still navigable */
	}
	return "";
}

// "KA_" + the record path with every character outside [A-Za-z0-9] turned
// into "_". Deterministic, so the same record always gets the same name.
export function fallbackNameFor(record) {
	const p = record && typeof record === "object" ? record.path : null;
	if (typeof p !== "string" || p === "") return "";
	return "KA_" + p.replace(/[^A-Za-z0-9]/g, "_");
}

// router.beforeResolve: async components are resolved by now, and the route
// has not committed, so `include` is ready before the first render (B6).
export function rememberRouteComponent(route, setLiveComponent) {
	try {
		const record = leafRecord(route);
		if (!record || !record.components) return;
		const name = componentNameOf(record.components.default, fallbackNameFor(record));
		if (name && typeof setLiveComponent === "function") setLiveComponent(record.path, name);
	} catch (e) {
		/* never block a navigation over a cache */
	}
}

// B7 (§3.2.1): a page whose leaf record says `meta: { keepAlive: false }` is
// never kept alive — pages with background side effects (webcam, polling)
// that a hidden instance would keep running.
export function keepAliveOptedOut(route) {
	const record = leafRecord(route);
	return !!(record && record.meta && record.meta.keepAlive === false);
}

function nameForRoute(route, components) {
	if (keepAliveOptedOut(route)) return "";
	const record = leafRecord(route);
	if (!record || typeof record.path !== "string") return "";
	const name = components[record.path];
	return typeof name === "string" ? name : "";
}

// Component names keep-alive should cache: every open tab's page, plus the
// page on screen (B6 — the taskbar opens its tab a tick after the route
// commits). A route that is not a taskbar page (/login) is never cached (B5).
export function keepAliveInclude(tabs, currentRoute, resolveRoute, components) {
	const out = [];
	if (!components || typeof components !== "object") return out;
	const add = (name) => {
		if (name && !out.includes(name)) out.push(name);
	};
	if (Array.isArray(tabs) && typeof resolveRoute === "function") {
		for (const tab of tabs) {
			if (!tab || typeof tab !== "object") continue;
			const target = tab.href || tab.key;
			if (typeof target !== "string" || target === "") continue;
			try {
				const route = resolveRoute(target);
				if (taskbarIsPage(route)) add(nameForRoute(route, components));
			} catch (e) {
				/* a tab whose path no longer resolves is simply not cached */
			}
		}
	}
	try {
		if (currentRoute && taskbarIsPage(currentRoute)) add(nameForRoute(currentRoute, components));
	} catch (e) {
		/* idem */
	}
	return out;
}

// <transition> (inside <fade-transition>, which wraps the layout's keep-alive)
// rewrites its real child's key to "__transition-<uid>-" + key BEFORE
// keep-alive renders (platforms/web/runtime/components/transition.js), so the
// keep-alive's cache key is the router-view :key with that prefix. The prefix
// is fixed per transition instance; strip it to compare with a tab key.
const TRANSITION_KEY_PREFIX = /^__transition-\d+-/;

// The cache key under which `key` is stored: the exact key if cached, else
// the "__transition-<uid>-"-prefixed key whose remainder equals `key` exactly
// ("/orders" never matches "__transition-7-/seller/orders"). When nothing is
// cached under either, `key` itself comes back — so callers must still check
// cache[result]. null only for a key that is not a non-empty string.
export function cacheKeyFor(keepAliveVm, key) {
	if (typeof key !== "string" || key === "") return null;
	try {
		const cache = keepAliveVm && keepAliveVm.cache;
		if (!cache || typeof cache !== "object") return key;
		if (cache[key]) return key;
		const keys = Array.isArray(keepAliveVm.keys) ? keepAliveVm.keys : Object.keys(cache);
		for (const k of keys) {
			if (typeof k === "string" && cache[k] && k.replace(TRANSITION_KEY_PREFIX, "") === key) return k;
		}
	} catch (e) {
		/* shapeless vm — fall through */
	}
	return key;
}

// Is anything actually cached for `key` (exact or prefixed)?
function isCached(keepAliveVm, key) {
	const found = cacheKeyFor(keepAliveVm, key);
	const cache = keepAliveVm && keepAliveVm.cache;
	return !!(found !== null && cache && typeof cache === "object" && cache[found]);
}

// Is this cache entry the page the keep-alive is showing right now?
function isOnScreen(keepAliveVm, entry) {
	const inst = entry && entry.componentInstance;
	const current = keepAliveVm && keepAliveVm._vnode;
	return !!(inst && current && current.componentInstance === inst);
}

// Drop one entry from a <keep-alive>'s cache and destroy its instance (B3).
// `key` is a tab key or a raw cache key (see cacheKeyFor). True if it went.
//
// Duck-typed: Vue 2.6 keeps the vnode in cache[key], Vue 2.7 keeps
// {name, tag, componentInstance}; both have .componentInstance, and both keep
// `keys` as an array.
//
// The instance ON SCREEN is left alone and false returned; the caller retries
// once the user is off it (evictOrDefer + DashboardLayout's flushEvictions).
// Destroying it now would leave a dead component rendered, and merely dropping
// it from the cache does not stick: the next keep-alive render (any change to
// `include` is one) puts the on-screen vnode straight back, and it would then
// be DEactivated, not destroyed, on the way out — reopening the closed tab
// would bring back its old state. Found by keepAliveIntegration.spec.js.
//
// Vue's own pruneCacheEntry tells "on screen" apart by vnode TAG, which two
// instances of the same page (B2) share — it would skip the other one.
// Comparing instances does not.
export function evictCached(keepAliveVm, key) {
	try {
		if (!keepAliveVm || typeof keepAliveVm !== "object") return false;
		const cache = keepAliveVm.cache;
		if (!cache || typeof cache !== "object") return false;
		const found = cacheKeyFor(keepAliveVm, key);
		if (found === null) return false;
		const entry = cache[found];
		if (!entry || isOnScreen(keepAliveVm, entry)) return false;
		cache[found] = null;
		delete cache[found];
		const keys = keepAliveVm.keys;
		if (Array.isArray(keys)) {
			const i = keys.indexOf(found);
			if (i > -1) keys.splice(i, 1);
		}
		const inst = entry.componentInstance;
		if (inst && typeof inst.$destroy === "function") {
			try {
				inst.$destroy();
			} catch (e) {
				/* the cache entry is gone either way */
			}
		}
		return true;
	} catch (e) {
		return false;
	}
}

// Evict every key that can go now; return the ones that must wait because
// they are on screen (still cached, not evicted). Keys no longer cached at all
// are simply done.
export function evictOrDefer(keepAliveVm, keys) {
	const later = [];
	for (const key of Array.isArray(keys) ? keys : []) {
		if (evictCached(keepAliveVm, key)) continue;
		if (isCached(keepAliveVm, key) && !later.includes(key)) later.push(key);
	}
	return later;
}

// Pending evictions that are still wanted: a key whose tab has come back
// (closing the only tab re-opens the page you are on) must not be evicted.
export function stillClosed(pending, tabs) {
	const open = new Set((Array.isArray(tabs) ? tabs : []).map((t) => (t && typeof t.key === "string" ? t.key : "")));
	return (Array.isArray(pending) ? pending : []).filter((k) => !open.has(String(k).replace(TRANSITION_KEY_PREFIX, "")));
}

// The <keep-alive> instance rendered somewhere under `vnode`, or null.
//
// Not a template ref: Vue 2 strips every data field except slot / props /
// listeners from an ABSTRACT component's vnode (vdom/create-component.js), and
// keep-alive is abstract — `<keep-alive ref="x">` never registers, `$refs.x`
// stays undefined. Nor is it in $children (abstract instances are not added).
// So the layout walks its own render tree: element children, and into each
// child component's rendered tree (the keep-alive sits inside
// <fade-transition> -> <transition>). Bounded, and never throws.
export function findKeepAlive(vnode) {
	try {
		return walkForKeepAlive(vnode);
	} catch (e) {
		return null;
	}
}

function walkForKeepAlive(vnode) {
	const stack = [vnode];
	let budget = 5000;
	while (stack.length && budget-- > 0) {
		const node = stack.pop();
		if (!node || typeof node !== "object") continue;
		const opts = node.componentOptions;
		const inst = node.componentInstance;
		if (opts && opts.Ctor && opts.Ctor.options && opts.Ctor.options.abstract && opts.Ctor.options.name === "keep-alive" && inst) {
			return inst;
		}
		// Recognised by the instance too: named keep-alive, or abstract AND
		// holding a cache object. <transition> is abstract with no cache, so
		// it is walked through, not returned.
		if (inst && typeof inst === "object" && inst.$options && typeof inst.$options === "object") {
			if (inst.$options.name === "keep-alive") return inst;
			if (inst.$options.abstract === true && inst.cache && typeof inst.cache === "object") return inst;
		}
		if (Array.isArray(node.children)) for (const c of node.children) stack.push(c);
		if (inst && inst._vnode) stack.push(inst._vnode);
	}
	return null;
}

// Keys present in prevTabs and absent from nextTabs, in prevTabs' order.
export function removedKeys(prevTabs, nextTabs) {
	const keyOf = (tab) => (tab && typeof tab === "object" && typeof tab.key === "string" ? tab.key : "");
	const next = new Set((Array.isArray(nextTabs) ? nextTabs : []).map(keyOf));
	const out = [];
	for (const tab of Array.isArray(prevTabs) ? prevTabs : []) {
		const k = keyOf(tab);
		if (k && !next.has(k) && !out.includes(k)) out.push(k);
	}
	return out;
}
